import React, { useState, useEffect, useCallback } from 'react'
import {
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Table,
  message,
  Dropdown,
  Tooltip,
  Card,
  Switch,
  Space,
  Tag
} from 'antd'
import styled from 'styled-components'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import minMax from 'dayjs/plugin/minMax'
import {
  EllipsisOutlined,
  InfoCircleOutlined,
  CalendarOutlined,
  FieldTimeOutlined,
  PlusCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  DownOutlined,
  UpOutlined,
  CopyOutlined,
  ScheduleOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import CommonHint from './common/Hint'
import { useAuth } from '../hooks/AuthContext'
import api from '../utils/api'
import { DatePicker as MobileDatePicker } from 'antd-mobile'
// ⚠️ 這支檔案有兩種完全相反的時間：
//   1. 使用者在 RangePicker 上挑的值 → 牆上時間 → fromPicker()
//   2. 後端回來的 start / end（ISO 絕對時刻）→ inTz()
// 原本兩種都寫 dayjs(x) 或 x.tz('Asia/Taipei')，在 UTC+8 以外的裝置上
// 會把「整天休假」存成 01:00 ~ 隔天 00:59，表格也就再也認不出「整天」。
import { fromPicker, inTz, now as tzNow } from '../utils/timezone'

dayjs.extend(customParseFormat)
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(minMax)

const { RangePicker } = DatePicker

const Container = styled.div`
  max-width: 800px;
  margin: auto;
  padding: 20px 0;
`

const Hint = styled.div`
  font-size: 14px;
  color: #666;
  background-color: #f5f5f5;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 12px;
  margin: 8px 0;
`

const TimeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
  margin-top: 16px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
    max-height: 400px;
    overflow-y: auto;
    padding-right: 8px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
    max-height: 320px;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`

const TimeSlot = styled.button`
  padding: 8px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  background: ${(props) => (props.selected ? '#bf1c5f' : '#f5f5f5')};
  color: ${(props) => (props.selected ? '#fff' : '#666')};
  cursor: pointer;
  transition: all 0.3s;
  width: 100%;
  font-size: 14px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const Legend = styled.div`
  display: flex;
  gap: 16px;
  margin: 16px 0;
  align-items: center;

  .legend-item {
    display: flex;
    align-items: center;
    gap: 8px;

    .color-box {
      width: 16px;
      height: 16px;
      border-radius: 4px;
    }
  }
`

const TimeSection = styled(Card)`
  margin-bottom: 16px;

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    h4 {
      margin: 0;
      font-weight: 500;
    }
  }
`

const CopyModal = styled(Modal)`
  .date-range {
    margin-bottom: 16px;
  }
`

const QuickActionCard = styled.div`
  .action-buttons {
    display: flex;
    gap: 12px;
    padding: 8px 0;
  }

  .ant-card-body {
    padding: 16px;
  }
`

const QuickActionButton = styled(Button)`
  width: 100%;
  height: 48px;
  border-radius: 8px;
  border: none;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.08);
  }

  .anticon {
    font-size: 18px;
  }

  &.full-day {
    background: linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%);
    color: #fff;
    padding: 0 5px;
    &:hover {
      color: #fff !important;
      background: linear-gradient(135deg, #ff8a8e 0%, #f9c0b4 100%) !important;
    }
  }

  &.range {
    background: linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%);
    color: #fff;
    padding: 0 5px;
    &:hover {
      color: #fff !important;
      background: linear-gradient(135deg, #917cd1 0%, #fab2eb 100%) !important;
    }
  }

  &.add-new {
    background: linear-gradient(135deg, #f899ac 0%, #f9c1cc 100%);
    color: #fff;
    padding: 0 5px;
    &:hover {
      color: #fff !important;
      background: linear-gradient(135deg, #f4758e 0%, #f4acba 100%) !important;
    }
  }
  @media (max-width: 576px) {
    height: 44px;
    font-size: 15px;
    .anticon {
      font-size: 16px;
    }
  }
`

const EmployeeDaysOff = ({ employeeId }) => {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isEditModalVisible, setIsEditModalVisible] = useState(false)
  const [daysOff, setDaysOff] = useState([])
  const [form] = Form.useForm()
  const [editForm] = Form.useForm()
  const [selectedYear, setSelectedYear] = useState(tzNow().year())
  const [editRecord, setEditRecord] = useState(null)
  const { user } = useAuth()
  const navigate = useNavigate()
  const businessId = user.businessId
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedTimeSlots, setSelectedTimeSlots] = useState({})
  const [showMidnight, setShowMidnight] = useState(false)
  const [isCopyModalVisible, setIsCopyModalVisible] = useState(false)
  const [copyDateRange, setCopyDateRange] = useState(null)
  const [quickActionModalVisible, setQuickActionModalVisible] = useState(false)
  const [quickActionType, setQuickActionType] = useState('fullDay')
  const [quickActionDateRange, setQuickActionDateRange] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [selectedDays, setSelectedDays] = useState([])
  const [showMobileStartPicker, setShowMobileStartPicker] = useState(false)
  const [showMobileEndPicker, setShowMobileEndPicker] = useState(false)
  const [mobileStartDate, setMobileStartDate] = useState(null)
  const [mobileEndDate, setMobileEndDate] = useState(null)
  const [showEditMobileStartPicker, setShowEditMobileStartPicker] =
    useState(false)
  const [showEditMobileEndPicker, setShowEditMobileEndPicker] = useState(false)
  const [editMobileStartDate, setEditMobileStartDate] = useState(null)
  const [editMobileEndDate, setEditMobileEndDate] = useState(null)
  const [showQuickMobileStartPicker, setShowQuickMobileStartPicker] =
    useState(false)
  const [showQuickMobileEndPicker, setShowQuickMobileEndPicker] =
    useState(false)
  const [quickMobileStartDate, setQuickMobileStartDate] = useState(null)
  const [quickMobileEndDate, setQuickMobileEndDate] = useState(null)

  const fetchDaysOff = useCallback(() => {
    api
      .get(`/api/employees/${businessId}/${employeeId}/dayoff`)
      .then((response) => {
        const updatedDaysOff = response.data.data
          .map((dayOff) => ({
            ...dayOff,
            start: dayjs(dayOff.start).tz('Asia/Taipei'),
            end: dayjs(dayOff.end).tz('Asia/Taipei')
          }))
          .sort((a, b) => (a.start.isBefore(b.start) ? 1 : -1))
        setDaysOff(updatedDaysOff)
      })
      .catch((error) => {
        console.error('獲取員工休假日錯誤:', error)
      })
  }, [businessId, employeeId])

  useEffect(() => {
    fetchDaysOff()
  }, [fetchDaysOff])

  const showModal = () => {
    setIsModalVisible(true)
  }

  const handleCancel = () => {
    setIsModalVisible(false)
    form.resetFields()
    setMobileStartDate(null)
    setMobileEndDate(null)
  }

  const handleEditCancel = () => {
    setIsEditModalVisible(false)
    setEditRecord(null)
    editForm.resetFields()
    setEditMobileStartDate(null)
    setEditMobileEndDate(null)
  }

  const handleFinish = (values) => {
    // fromPicker：保留使用者看到的牆上時間，只換時區標籤。
    // 用 .tz('Asia/Taipei') 是反過來的 —— 它保留「瞬間」而改寫牆上時間，
    // 店家人在 UTC+7 挑的 00:00 會被存成台灣的 01:00。
    const newDayOff = {
      name: values.name,
      start: fromPicker(values.date[0]).format(),
      end: fromPicker(values.date[1]).format()
    }

    console.log('發送的時間格式:', newDayOff)

    api
      .post(`/api/employees/${businessId}/${employeeId}/dayoff`, newDayOff)
      .then(() => {
        message.success('新增休假日成功')
        setIsModalVisible(false)
        fetchDaysOff()
      })
      .catch((error) => {
        message.error(
          error?.response?.data?.error || '新增休假日失敗，請稍後再試'
        )
        console.error('Error adding employee day off:', error)
      })
  }

  const handleEditFinish = (values) => {
    const updatedDayOff = {
      name: values.name,
      start: fromPicker(values.date[0]).format(),
      end: fromPicker(values.date[1]).format()
    }
    console.log('編輯的時間格式:', updatedDayOff)

    api
      .put(
        `/api/employees/${businessId}/${employeeId}/dayoff/${editRecord._id}`,
        updatedDayOff
      )
      .then(() => {
        message.success('更新休假日成功')
        setIsEditModalVisible(false)
        setEditRecord(null)
        fetchDaysOff()
      })
      .catch((error) => {
        message.error(
          error?.response?.data?.error || '更新休假日失敗，請稍後再試'
        )
        console.error('Error updating employee day off:', error)
      })
  }

  const handleYearChange = (value) => {
    setSelectedYear(value)
  }

  const handleEdit = (record) => {
    setEditRecord(record)
    editForm.setFieldsValue({
      name: record.name,
      date: [inTz(record.start), inTz(record.end)]
    })
    setIsEditModalVisible(true)
  }

  const handleDelete = (id) => {
    api
      .delete(`/api/employees/${businessId}/${employeeId}/dayoff/${id}`)
      .then(() => {
        message.success('刪除休假日成功')
        fetchDaysOff()
      })
      .catch((error) => {
        // 後端會擋掉班表來源的休假，把它的說明直接顯示出來比較好懂
        message.error(
          error?.response?.data?.error || '刪除休假日失敗，請稍後再試'
        )
        console.error('Error deleting employee day off:', error)
      })
  }

  /**
   * 這筆休假是不是月班表排出來的。
   * 班表是常態排班的主控，這頁只負責班表管不到的臨時／部分時段休假，
   * 所以班表來源的只顯示、不給改，避免兩邊各改一半對不起來。
   */
  const isFromSchedule = (record) => record?.source === 'schedule'

  const columns = [
    {
      title: '日期',
      dataIndex: 'start',
      key: 'date',
      render: (text, record) => {
        // 後端存的是絕對時刻，一定要換成店家時區再判斷「是不是同一天」，
        // 否則台灣的整天休假（00:00~23:59）在 UTC+7 會讀成 23:00~22:59，
        // 一天的假被畫成跨兩天的區間
        const start = inTz(record.start)
        const end = inTz(record.end)

        if (start.isSame(end, 'day')) {
          if (
            start.format('HH:mm') === '00:00' &&
            end.format('HH:mm') === '23:59'
          ) {
            return start.format('YYYY-MM-DD')
          }
          return (
            <div style={{ whiteSpace: 'nowrap' }}>
              {start.format('YYYY-MM-DD')}
              <br />
              {start.format('HH:mm')} - {end.format('HH:mm')}
            </div>
          )
        }
        return (
          <div style={{ whiteSpace: 'nowrap' }}>
            {start.format('YYYY-MM-DD')}
            <br />
            {end.format('YYYY-MM-DD')}
          </div>
        )
      }
    },
    {
      title: '休假名稱',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (text, record) => (
        <Space size={4} wrap>
          <span>{text}</span>
          {isFromSchedule(record) && (
            <Tooltip title='這天在月班表上排了假別，要改請到「班表」'>
              <Tag color='purple' style={{ margin: 0 }}>
                來自班表
              </Tag>
            </Tooltip>
          )}
        </Space>
      )
    },
    {
      title: '時段',
      key: 'timeSlot',
      render: (_, record) => {
        const start = inTz(record.start)
        const end = inTz(record.end)

        if (
          start.format('HH:mm') === '00:00' &&
          end.format('HH:mm') === '23:59'
        ) {
          return <span style={{ whiteSpace: 'nowrap' }}>整天</span>
        }
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            {start.format('HH:mm')} - {end.format('HH:mm')}
          </span>
        )
      }
    },
    {
      title: '',
      key: 'action',
      width: 50,
      render: (_, record) => (
        <Dropdown menu={getActionMenu(record)} trigger={['click']}>
          <Button type='text' icon={<EllipsisOutlined />} />
        </Dropdown>
      )
    }
  ]

  const getResponsiveColumns = () => {
    if (window.innerWidth <= 576) {
      return [
        columns[0], // 日期
        columns[1], // 休假名稱
        columns[3] // 操作
      ]
    }
    return columns
  }

  const hintText = (
    <>
      按日計算：
      <br />
      以24小時為單位，點擊日期自動帶入，例如：00:00~23:59，即為1天
      <br />
      按時計算：
      <br />
      如11:00開始休假，14:00恢復上班，請選擇11:00~13:59
    </>
  )

  // 生成時間選項
  const generateTimeSlots = () => {
    try {
      const slots = []
      const start = dayjs().startOf('day')
      const startHour = showMidnight ? 0 : 9
      const totalSlots = showMidnight ? 24 * 6 : 15 * 6

      for (let i = 0; i < totalSlots; i++) {
        try {
          const time = start.add(startHour * 60 + i * 10, 'minute')
          if (time.isValid()) {
            slots.push(time.format('HH:mm'))
          }
        } catch (e) {
          console.error('Error generating time slot:', e)
        }
      }

      return slots
    } catch (e) {
      console.error('Error in generateTimeSlots:', e)
      return []
    }
  }

  const timeSlots = generateTimeSlots()

  const handleTimeSlotClick = (slot) => {
    if (!selectedDate) return

    const dateKey = selectedDate.format('YYYY-MM-DD')
    const currentSlots = selectedTimeSlots[dateKey] || {}

    setSelectedTimeSlots((prev) => ({
      ...prev,
      [dateKey]: {
        ...currentSlots,
        [slot]: !currentSlots[slot]
      }
    }))
  }

  const handleDateChange = (date) => {
    setSelectedDate(date)
  }

  const handleSaveTimeSlots = () => {
    if (!selectedDate) return

    const dateKey = selectedDate.format('YYYY-MM-DD')
    const selectedSlots = selectedTimeSlots[dateKey] || {}

    // 將選中的時段轉換為連續的時間段
    const timeRanges = Object.entries(selectedSlots)
      .filter(([_, selected]) => selected)
      .map(([time]) => time)
      .sort()
      .reduce((ranges, time) => {
        if (ranges.length === 0) {
          return [[time, time]]
        }

        const lastRange = ranges[ranges.length - 1]
        const lastTime = lastRange[1]
        const currentTime = dayjs(time, 'HH:mm')
        const prevTime = dayjs(lastTime, 'HH:mm')

        if (currentTime.diff(prevTime, 'minute') === 10) {
          lastRange[1] = time
          return ranges
        }

        return [...ranges, [time, time]]
      }, [])

    // 轉換為 API 所需的格式
    const timeSlots = timeRanges.map(([start, end]) => ({
      start,
      end
    }))

    // 調用新的 API
    api
      .put(`/api/employees/${businessId}/${employeeId}/dayoff/date`, {
        date: dateKey,
        timeSlots
      })
      .then(() => {
        message.success('儲存休假時段成功')
        fetchDaysOff()
        setSelectedTimeSlots((prev) => ({
          ...prev,
          [dateKey]: {}
        }))
      })
      .catch((error) => {
        message.error('儲存休假時段失敗')
        console.error('Error saving time slots:', error)
      })
  }

  // 修改為兩個不同的處理函數
  // 1. 用於表格操作選單的複製功能
  const handleCopyRecord = (record) => {
    // 一樣是絕對時刻 → 店家時區，不然複製出來的假會落在前一天的 23:00
    const recordStart = inTz(record.start)
    setSelectedDate(recordStart)
    const dateKey = recordStart.format('YYYY-MM-DD')

    // 設置選中的時段
    const slots = {}
    let currentTime = recordStart
    const endTime = inTz(record.end)

    while (currentTime.isSameOrBefore(endTime, 'minute')) {
      slots[currentTime.format('HH:mm')] = true
      currentTime = currentTime.add(10, 'minute')
    }

    setSelectedTimeSlots((prev) => ({
      ...prev,
      [dateKey]: slots
    }))

    setIsCopyModalVisible(true)
  }

  // 2. 用於時段下方按鈕的複製功能
  const handleCopyCurrentSlots = () => {
    if (!selectedDate) return
    setIsCopyModalVisible(true)
  }

  const handleCopyConfirm = async () => {
    if (!copyDateRange || !selectedDate) return

    try {
      // 使用者挑的日期區間 → 保留牆上時間（fromPicker），
      // 用 .tz() 會把 UTC+7 的午夜推成台灣的 01:00，複製的起訖日就整個位移
      const startDate = fromPicker(copyDateRange[0])
      const endDate = fromPicker(copyDateRange[1])

      if (!startDate.isValid() || !endDate.isValid()) {
        message.error('无效的日期范围')
        return
      }

      const sourceDate = selectedDate.format('YYYY-MM-DD')
      const selectedSlots = selectedTimeSlots[sourceDate] || {}

      // 将选中的时段转换为连续的时间段
      const timeRanges = Object.entries(selectedSlots)
        .filter(([_, selected]) => selected)
        .map(([time]) => time)
        .sort()
        .reduce((ranges, time) => {
          if (ranges.length === 0) {
            return [[time, time]]
          }

          const lastRange = ranges[ranges.length - 1]
          const lastTime = lastRange[1]
          const currentTime = dayjs(time, 'HH:mm')
          const prevTime = dayjs(lastTime, 'HH:mm')

          if (currentTime.diff(prevTime, 'minute') === 10) {
            lastRange[1] = time
            return ranges
          }

          return [...ranges, [time, time]]
        }, [])

      const timeSlots = timeRanges.map(([start, end]) => ({
        start,
        end
      }))

      // 生成日期范围内的所有日期
      const dates = []
      let currentDate = startDate.clone()

      while (currentDate.valueOf() <= endDate.valueOf()) {
        dates.push(currentDate.format('YYYY-MM-DD'))
        currentDate = currentDate.add(1, 'day')
      }

      // 对每个目标日期执行复制
      await Promise.all(
        dates.map((date) =>
          api.put(`/api/employees/${businessId}/${employeeId}/dayoff/date`, {
            date,
            timeSlots
          })
        )
      )

      message.success('复制时段成功')
      setIsCopyModalVisible(false)
      setCopyDateRange(null)
      fetchDaysOff()
    } catch (error) {
      console.error('复制时段失败：', error)
      message.error('复制时段失败')
    }
  }

  // 快速操作 - 整日休假或區間休假
  const handleQuickAction = (type) => {
    setQuickActionType(type)
    setQuickActionModalVisible(true)
  }

  const handleQuickActionConfirm = () => {
    if (!quickActionDateRange) {
      message.error('請選擇日期')
      return
    }

    if (quickActionType === 'fullDay') {
      // 整日休假
      // 跟 handleFinish 走同一種格式（帶時區的 ISO），
      // 原本送的是沒有時區資訊的裸字串，同一個功能出現兩種慣例
      const quickStart = fromPicker(quickActionDateRange[0])
      const newDayOff = {
        name: '整日休假',
        start: quickStart.startOf('day').format(),
        end: quickStart.hour(23).minute(59).second(0).millisecond(0).format()
      }

      api
        .post(`/api/employees/${businessId}/${employeeId}/dayoff`, newDayOff)
        .then(() => {
          message.success('新增整日休假成功')
          fetchDaysOff()
        })
        .catch((error) => {
          message.error('新增休假失敗')
          console.error(error)
        })
    } else {
      // 區間休假
      const newDayOff = {
        name: '快速新增區間休假',
        start: fromPicker(quickActionDateRange[0]).startOf('day').format(),
        end: fromPicker(quickActionDateRange[1])
          .hour(23)
          .minute(59)
          .second(0)
          .millisecond(0)
          .format()
      }

      api
        .post(`/api/employees/${businessId}/${employeeId}/dayoff`, newDayOff)
        .then(() => {
          message.success('新增區間休假成功')
          fetchDaysOff()
        })
        .catch((error) => {
          message.error('新增休假失敗')
          console.error(error)
        })
    }

    setQuickActionModalVisible(false)
    setQuickActionDateRange(null)
  }

  // 將休假記錄轉換為時間格子格式
  useEffect(() => {
    if (!selectedDate) return

    const dateStr = selectedDate.format('YYYY-MM-DD')
    const newTimeSlots = {}

    try {
      daysOff.forEach((dayOff) => {
        // 确保创建有效的 dayjs 对象，并进行时区转换
        let startDate, endDate

        try {
          startDate = dayjs(dayOff.start).tz('Asia/Taipei')
          endDate = dayjs(dayOff.end).tz('Asia/Taipei')

          // 验证时间对象是否有效
          if (!startDate.isValid() || !endDate.isValid()) {
            console.warn('Invalid date objects:', {
              start: dayOff.start,
              end: dayOff.end
            })
            return // 跳过这条记录
          }
        } catch (e) {
          console.error('Error parsing dates:', e)
          return // 跳过这条记录
        }

        // 检查是否是选中日期的休假
        if (startDate.format('YYYY-MM-DD') === dateStr) {
          let currentTime = startDate.clone()
          const endOfCurrentDay = startDate.clone().endOf('day')
          const effectiveEndTime = endDate.isSame(startDate, 'day')
            ? endDate
            : endOfCurrentDay

          // 使用普通的比较逻辑替代 isSameOrBefore
          while (currentTime.valueOf() <= effectiveEndTime.valueOf()) {
            try {
              const timeStr = currentTime.format('HH:mm')
              newTimeSlots[timeStr] = true
              currentTime = currentTime.add(10, 'minute')
            } catch (e) {
              console.error('Error in time slot processing:', e)
              break
            }
          }
        }
      })

      setSelectedTimeSlots((prev) => ({
        ...prev,
        [dateStr]: newTimeSlots
      }))
    } catch (error) {
      console.error('Error processing time slots:', error)
      message.error('处理时间格子时出错')
    }
  }, [selectedDate, daysOff])

  const getActionMenu = (record) => {
    // 班表排出來的休假在這裡是唯讀的：改了也會在下次存班表時被覆蓋回去，
    // 所以直接把人帶去班表，而不是給一個會失敗的編輯按鈕。
    if (isFromSchedule(record)) {
      return {
        items: [
          {
            key: 'goSchedule',
            icon: <ScheduleOutlined />,
            label: '到班表修改這天',
            onClick: () => navigate('/schedule')
          }
        ]
      }
    }
    return {
    items: [
      {
        key: 'edit',
        icon: <EditOutlined />,
        label: '編輯',
        onClick: () => handleEdit(record)
      },
      {
        key: 'copy',
        icon: <CopyOutlined />,
        label: '複製',
        onClick: () => handleCopyRecord(record)
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: '刪除',
        danger: true,
        onClick: () => handleDelete(record._id)
      }
    ]
    }
  }

  // 确保在组件顶部正确初始化 dayjs
  useEffect(() => {
    // 验证 dayjs 配置
    try {
      const testDate = tzNow()
      if (!testDate.isValid()) {
        console.error('dayjs initialization failed')
      }
      if (!testDate.tz) {
        console.error('timezone plugin not properly initialized')
      }
    } catch (e) {
      console.error('Error checking dayjs configuration:', e)
    }
  }, [])

  const isMobileView = () => {
    return window.innerWidth <= 820
  }

  const handleMobileStartConfirm = (date) => {
    setMobileStartDate(dayjs(date))
    form.setFieldsValue({
      date: [dayjs(date), form.getFieldValue('date')?.[1]]
    })
    setShowMobileStartPicker(false)
  }

  const handleMobileEndConfirm = (date) => {
    setMobileEndDate(dayjs(date))
    form.setFieldsValue({
      date: [form.getFieldValue('date')?.[0], dayjs(date)]
    })
    setShowMobileEndPicker(false)
  }

  const handleEditMobileStartConfirm = (date) => {
    setEditMobileStartDate(dayjs(date))
    editForm.setFieldsValue({
      date: [dayjs(date), editForm.getFieldValue('date')?.[1]]
    })
    setShowEditMobileStartPicker(false)
  }

  const handleEditMobileEndConfirm = (date) => {
    setEditMobileEndDate(dayjs(date))
    editForm.setFieldsValue({
      date: [editForm.getFieldValue('date')?.[0], dayjs(date)]
    })
    setShowEditMobileEndPicker(false)
  }

  const handleQuickMobileStartConfirm = (date) => {
    const newDate = dayjs(date)
    setQuickMobileStartDate(newDate)
    setQuickActionDateRange((prev) => [newDate, prev?.[1] || newDate])
    setShowQuickMobileStartPicker(false)
  }

  const handleQuickMobileEndConfirm = (date) => {
    const newDate = dayjs(date)
    setQuickMobileEndDate(newDate)
    setQuickActionDateRange((prev) => [prev?.[0], newDate])
    setShowQuickMobileEndPicker(false)
  }

  return (
    <Container>
      <CommonHint
        tone='info'
        title='常態排班與例休請到「班表」設定'
        action={
          <Button
            size='small'
            icon={<ScheduleOutlined />}
            onClick={() => navigate('/schedule')}
          >
            開啟班表
          </Button>
        }
      >
        這一頁放的是班表管不到的臨時休假與個人行程（例如 12:00-15:00
        外出拍片、一天有兩段行程）。整天的休假在這裡新增會自動標到班表上；
        標著「來自班表」的則要回班表修改。
      </CommonHint>

      <QuickActionCard>
        <div className='action-buttons'>
          <QuickActionButton
            className='full-day'
            onClick={() => handleQuickAction('fullDay')}
          >
            <CalendarOutlined />
            整日休假
          </QuickActionButton>

          <QuickActionButton
            className='range'
            onClick={() => handleQuickAction('range')}
          >
            <FieldTimeOutlined />
            區間休假
          </QuickActionButton>

          <QuickActionButton className='add-new' onClick={showModal}>
            <PlusCircleOutlined />
            新增休假
          </QuickActionButton>
        </div>
      </QuickActionCard>

      <Card title='選擇與查看休假時段'>
        <DatePicker
          value={selectedDate}
          onChange={handleDateChange}
          format='YYYY-MM-DD'
          placeholder='選擇日期'
          style={{ width: '100%', marginBottom: '16px' }}
        />

        {selectedDate && (
          <>
            <Hint>
              <div className='flex justify-between items-center mb-2'>
                <div>使用說明：</div>
                <Button
                  type='text'
                  onClick={() => setIsCollapsed(!isCollapsed)}
                  icon={isCollapsed ? <DownOutlined /> : <UpOutlined />}
                >
                  {isCollapsed ? '展開' : '收起'}
                </Button>
              </div>

              {!isCollapsed && (
                <>
                  • 點選時段格子可切換該時段的營業/休息狀態
                  <br />
                  •
                  系統會將所選時段納入休息時間。例如：選擇14:00作為休息結束時間，代表14:00仍在休息，14:01才開始營業
                  <br />•
                  預約時段若剛好在休假開始前結束則允許（例：17:00的預約到18:00結束可以），但若延伸到休假時間內則會被過濾（例：17:30的預約會被過濾）
                </>
              )}
            </Hint>

            <div style={{ marginBottom: '16px' }}>
              <Switch
                checked={showMidnight}
                onChange={setShowMidnight}
                checkedChildren='顯示凌晨時段'
                unCheckedChildren='隱藏凌晨時段'
              />
            </div>

            <Legend>
              <div className='legend-item'>
                <div className='color-box' style={{ background: '#bf1c5f' }} />
                <span>休假時段</span>
              </div>
              <div className='legend-item'>
                <div className='color-box' style={{ background: '#f5f5f5' }} />
                <span>工作時段</span>
              </div>
            </Legend>

            <TimeGrid>
              {generateTimeSlots().map((slot) => {
                const dateKey = selectedDate.format('YYYY-MM-DD')
                const isSelected = selectedTimeSlots[dateKey]?.[slot]

                return (
                  <TimeSlot
                    key={slot}
                    selected={isSelected}
                    onClick={() => handleTimeSlotClick(slot)}
                  >
                    {slot}
                  </TimeSlot>
                )
              })}
            </TimeGrid>

            <Space
              style={{ marginTop: '16px', width: '100%' }}
              direction='vertical'
            >
              <Button
                type='primary'
                onClick={handleSaveTimeSlots}
                style={{
                  width: '100%',
                  height: '40px',
                  background: '#bf1c5f',
                  borderColor: '#bf1c5f'
                }}
              >
                儲存休假時段
              </Button>
              <Button
                onClick={handleCopyCurrentSlots}
                style={{ width: '100%', height: '40px' }}
              >
                複製時段到其他日期
              </Button>
            </Space>
          </>
        )}
      </Card>

      {/* 複製時段 Modal */}
      <CopyModal
        title='複製時段'
        open={isCopyModalVisible}
        onOk={handleCopyConfirm}
        onCancel={() => {
          setIsCopyModalVisible(false)
          setCopyDateRange(null)
        }}
        okText='確認'
        cancelText='取消'
      >
        <div className='date-range'>
          <h4>選擇要複製到的日期範圍：</h4>
          <RangePicker
            style={{ width: '100%' }}
            value={copyDateRange}
            onChange={setCopyDateRange}
          />
        </div>
      </CopyModal>

      {/* 快速操作 Modal */}
      <Modal
        title={
          quickActionType === 'fullDay'
            ? '快速新增整日休假'
            : '快速新增區間休假'
        }
        open={quickActionModalVisible}
        onOk={handleQuickActionConfirm}
        onCancel={() => {
          setQuickActionModalVisible(false)
          setQuickActionDateRange(null)
          setQuickMobileStartDate(null)
          setQuickMobileEndDate(null)
        }}
        okText='確認'
        cancelText='取消'
      >
        {isMobileView() ? (
          <div>
            {quickActionType === 'fullDay' ? (
              // 整日休假 - 單日選擇
              <>
                <Button
                  block
                  onClick={() => setShowQuickMobileStartPicker(true)}
                  style={{ marginBottom: '16px' }}
                >
                  {quickMobileStartDate
                    ? quickMobileStartDate.format('YYYY-MM-DD')
                    : '選擇休假日期'}
                </Button>
                <MobileDatePicker
                  visible={showQuickMobileStartPicker}
                  onClose={() => setShowQuickMobileStartPicker(false)}
                  precision='day'
                  onConfirm={(date) => {
                    const newDate = dayjs(date)
                    setQuickMobileStartDate(newDate)
                    setQuickActionDateRange([newDate, newDate])
                    setShowQuickMobileStartPicker(false)
                  }}
                  min={tzNow().toDate()}
                  max={new Date(2030, 11, 31)}
                />
              </>
            ) : (
              // 區間休假 - 日期範圍選擇
              <>
                <Space direction='vertical' style={{ width: '100%' }}>
                  <Button
                    block
                    onClick={() => setShowQuickMobileStartPicker(true)}
                  >
                    {quickMobileStartDate
                      ? quickMobileStartDate.format('YYYY-MM-DD')
                      : '選擇開始日期'}
                  </Button>
                  <Button
                    block
                    onClick={() => setShowQuickMobileEndPicker(true)}
                  >
                    {quickMobileEndDate
                      ? quickMobileEndDate.format('YYYY-MM-DD')
                      : '選擇結束日期'}
                  </Button>
                </Space>

                <MobileDatePicker
                  visible={showQuickMobileStartPicker}
                  onClose={() => setShowQuickMobileStartPicker(false)}
                  precision='day'
                  onConfirm={handleQuickMobileStartConfirm}
                  min={tzNow().toDate()}
                  max={new Date(2030, 11, 31)}
                />

                <MobileDatePicker
                  visible={showQuickMobileEndPicker}
                  onClose={() => setShowQuickMobileEndPicker(false)}
                  precision='day'
                  onConfirm={handleQuickMobileEndConfirm}
                  min={
                    quickMobileStartDate
                      ? quickMobileStartDate.toDate()
                      : tzNow().toDate()
                  }
                  max={new Date(2030, 11, 31)}
                />
              </>
            )}
          </div>
        ) : // 桌面版保持原有的 DatePicker/RangePicker
        quickActionType === 'fullDay' ? (
          <DatePicker
            style={{ width: '100%' }}
            value={quickActionDateRange?.[0]}
            onChange={(date) =>
              setQuickActionDateRange(date ? [date, date] : null)
            }
          />
        ) : (
          <RangePicker
            style={{ width: '100%' }}
            value={quickActionDateRange}
            onChange={setQuickActionDateRange}
          />
        )}
      </Modal>

      <Modal
        title='新增休假具體時段'
        open={isModalVisible}
        footer={null}
        style={{ top: 20 }}
        onCancel={handleCancel}
      >
        <Form form={form} layout='vertical' onFinish={handleFinish}>
          <Form.Item
            name='name'
            label='休假日名稱'
            rules={[{ required: true, message: '請輸入休假日名稱' }]}
          >
            <Input placeholder='請輸入休假日名稱' />
          </Form.Item>
          <Form.Item
            name='date'
            label={
              <span>
                日期
                <Tooltip title={hintText}>
                  <InfoCircleOutlined style={{ marginLeft: '8px' }} />
                </Tooltip>
              </span>
            }
            rules={[{ required: true, message: '請選擇日期和時間' }]}
          >
            {isMobileView() ? (
              <div>
                <Space direction='vertical' style={{ width: '100%' }}>
                  <Button block onClick={() => setShowMobileStartPicker(true)}>
                    {mobileStartDate
                      ? mobileStartDate.format('YYYY-MM-DD HH:mm')
                      : '選擇開始時間'}
                  </Button>
                  <Button block onClick={() => setShowMobileEndPicker(true)}>
                    {mobileEndDate
                      ? mobileEndDate.format('YYYY-MM-DD HH:mm')
                      : '選擇結束時間'}
                  </Button>
                </Space>

                <MobileDatePicker
                  visible={showMobileStartPicker}
                  onClose={() => setShowMobileStartPicker(false)}
                  precision='minute'
                  onConfirm={handleMobileStartConfirm}
                  min={tzNow().toDate()}
                  max={new Date(2030, 11, 31)}
                />

                <MobileDatePicker
                  visible={showMobileEndPicker}
                  onClose={() => setShowMobileEndPicker(false)}
                  precision='minute'
                  onConfirm={handleMobileEndConfirm}
                  min={mobileStartDate ? mobileStartDate.toDate() : tzNow().toDate()}
                  max={new Date(2030, 11, 31)}
                />
              </div>
            ) : (
              <RangePicker
                style={{ width: '100%' }}
                format='YYYY-MM-DD HH:mm'
                showTime={{
                  format: 'HH:mm',
                  defaultValue: [
                    dayjs('00:00:00', 'HH:mm:ss'),
                    dayjs('23:59:59', 'HH:mm:ss')
                  ]
                }}
                separator='至'
                placeholder={['開始時間', '結束時間']}
              />
            )}
          </Form.Item>
          <Form.Item>
            <Button type='primary' htmlType='submit'>
              新增休假
            </Button>
            <Button onClick={handleCancel} style={{ marginLeft: 8 }}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title='編輯休假日'
        open={isEditModalVisible}
        style={{ top: 20 }}
        footer={null}
        onCancel={handleEditCancel}
      >
        <Form form={editForm} layout='vertical' onFinish={handleEditFinish}>
          <Form.Item
            name='name'
            label='休假日名稱'
            rules={[{ required: true, message: '請輸入休假日名稱' }]}
          >
            <Input placeholder='請輸入休假日名稱' />
          </Form.Item>
          <Form.Item
            name='date'
            label='日期'
            rules={[{ required: true, message: '請選擇日期和時間' }]}
          >
            {isMobileView() ? (
              <div>
                <Space direction='vertical' style={{ width: '100%' }}>
                  <Button
                    block
                    onClick={() => setShowEditMobileStartPicker(true)}
                  >
                    {editMobileStartDate
                      ? editMobileStartDate.format('YYYY-MM-DD HH:mm')
                      : '選擇開始時間'}
                  </Button>
                  <Button
                    block
                    onClick={() => setShowEditMobileEndPicker(true)}
                  >
                    {editMobileEndDate
                      ? editMobileEndDate.format('YYYY-MM-DD HH:mm')
                      : '選擇結束時間'}
                  </Button>
                </Space>

                <MobileDatePicker
                  visible={showEditMobileStartPicker}
                  onClose={() => setShowEditMobileStartPicker(false)}
                  precision='minute'
                  onConfirm={handleEditMobileStartConfirm}
                  min={tzNow().toDate()}
                  max={new Date(2030, 11, 31)}
                />

                <MobileDatePicker
                  visible={showEditMobileEndPicker}
                  onClose={() => setShowEditMobileEndPicker(false)}
                  precision='minute'
                  onConfirm={handleEditMobileEndConfirm}
                  min={
                    editMobileStartDate
                      ? editMobileStartDate.toDate()
                      : tzNow().toDate()
                  }
                  max={new Date(2030, 11, 31)}
                />
              </div>
            ) : (
              <RangePicker
                style={{ width: '100%' }}
                format='YYYY-MM-DD HH:mm'
                showTime={{
                  format: 'HH:mm',
                  defaultValue: [
                    dayjs('00:00:00', 'HH:mm:ss'),
                    dayjs('23:59:59', 'HH:mm:ss')
                  ]
                }}
                separator='至'
                placeholder={['開始時間', '結束時間']}
              />
            )}
          </Form.Item>
          <Form.Item>
            <Button type='primary' htmlType='submit'>
              保存
            </Button>
            <Button onClick={handleEditCancel} style={{ marginLeft: 8 }}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* 休假記錄列表 */}
      <Card title='休假記錄' style={{ marginTop: '24px' }}>
        <Table
          dataSource={daysOff}
          columns={getResponsiveColumns()}
          rowKey='_id'
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: daysOff.length,
            onChange: (page) => setCurrentPage(page),
            showTotal: (total) => `共 ${total} 筆`,
            showSizeChanger: false,
            showQuickJumper: true,
            position: ['bottomCenter'],
            size: 'small',
            locale: {
              items_per_page: '筆/頁',
              jump_to: '跳至',
              jump_to_confirm: '確定',
              page: '頁',
              prev_page: '上一頁',
              next_page: '下一頁',
              prev_5: '向前 5 頁',
              next_5: '向後 5 頁'
            }
          }}
          size='small'
          scroll={{ x: true }}
        />
      </Card>
    </Container>
  )
}

export default EmployeeDaysOff
